import { createRng } from './rng';

/**
 * Solara "Recovery Audit" findings (built in-house) — the back-office side of AP.
 *
 * Recovery audit finds AP (accounts-payable) overpayments, duplicate invoices,
 * VAT under-recovery, missing GLN, and late-TDD risk. It's a built-in Solara module
 * differentiator vs. raw Peppol clearing-house competitors and is exposed
 * to the customer at /recovery-audit, with admin-side roll-ups in /tickstar.
 */

export type RecoveryAuditCategory =
  | 'duplicate-payment'
  | 'duplicate-invoice'
  | 'vat-recovery-gap'
  | 'price-variance'
  | 'currency-mismatch'
  | 'missing-gln'
  | 'late-tdd'
  | 'overpayment'
  | 'credit-note-due'
  | 'supplier-master-mismatch';

export type RecoveryAuditStatus = 'open' | 'in-review' | 'recovered' | 'dismissed';

export interface RecoveryAuditFinding {
  findingId: string;
  category: RecoveryAuditCategory;
  /** Plain-language headline. */
  titleEn: string;
  titleAr: string;
  /** ISO 4217, always AED for UAE pilot. */
  currency: 'AED';
  /** Recoverable amount (gross AED). */
  recoverableAed: number;
  /** Confidence score from the Solara ML model. */
  confidence: 'low' | 'medium' | 'high';
  status: RecoveryAuditStatus;
  /** Affected invoice numbers (1–3 typical). */
  invoiceRefs: string[];
  /** Affected supplier (legal name). */
  supplier: string;
  detectedAt: string;
  ageDays: number;
  /** Optional next-step recommendation shown in the UI. */
  recommendation: string;
}

const TITLES: Record<RecoveryAuditCategory, { en: string; ar: string }> = {
  'duplicate-payment':       { en: 'Duplicate payment detected',                          ar: 'كُشِف عن دفعة مكررة' },
  'duplicate-invoice':       { en: 'Duplicate invoice number from same supplier',         ar: 'رقم فاتورة مكرر من نفس المورد' },
  'vat-recovery-gap':        { en: 'VAT under-recovered on standard-rated input',         ar: 'استرداد ضريبة قيمة مضافة ناقص على مدخلات بمعدل قياسي' },
  'price-variance':          { en: 'Unit-price variance > 8% vs. PO',                     ar: 'انحراف سعر الوحدة > 8 ٪ مقابل أمر الشراء' },
  'currency-mismatch':       { en: 'Foreign-currency invoice booked in AED at wrong FX',  ar: 'فاتورة بعملة أجنبية مرحّلة بالدرهم بسعر صرف غير صحيح' },
  'missing-gln':             { en: 'Buyer GLN missing on Peppol envelope',                ar: 'رقم GLN للمشتري مفقود في غلاف Peppol' },
  'late-tdd':                { en: 'TDD submission window closes in < 48 h',              ar: 'نافذة تقديم TDD تُغلق خلال أقل من ٤٨ ساعة' },
  'overpayment':             { en: 'Cumulative overpayment vs. contract cap',             ar: 'فائض مدفوعات تراكمي مقابل سقف العقد' },
  'credit-note-due':         { en: 'Credit-note expected vs. quality-rejection log',      ar: 'إشعار دائن متوقع مقابل سجل رفض الجودة' },
  'supplier-master-mismatch':{ en: 'Bank IBAN mismatch with supplier master',             ar: 'رقم IBAN لا يطابق سجل المورد الأساسي' },
};

const SUPPLIERS = [
  'Al Futtaim Logistics LLC',
  'Etisalat by e&',
  'DEWA',
  'Emirates Print FZE',
  'Khalid Al Rostamani Group',
  'Dubai Refreshments',
  'Al Tayer Group',
  'Apparel Group LLC',
  'Sharaf DG LLC',
  'Tristar Engineering FZE',
  'Aldar Engineering Services',
  'Spinneys Dubai',
  'Lulu Hypermarket FZE',
  'Damac Maintenance Services',
  'Mashreq Facilities Management',
];

const RECOMMENDATIONS: Record<RecoveryAuditCategory, string> = {
  'duplicate-payment':       'Initiate refund request to supplier; flag duplicate in ERP.',
  'duplicate-invoice':       'Withhold posting; require credit note before reconciliation.',
  'vat-recovery-gap':        'Adjust input-tax claim in next VAT return (FTA Form 201).',
  'price-variance':          'Open price-variance dispute with the supplier.',
  'currency-mismatch':       'Re-book at FTA-published rate; reverse mis-stated AED amount.',
  'missing-gln':             'Trigger Peppol-directory enrichment; resend AS4 with corrected GLN.',
  'late-tdd':                'Force-submit TDD via Solara Compliance Centre to avoid AED 1k penalty.',
  'overpayment':             'Issue debit note; reduce next payment run by recoverable amount.',
  'credit-note-due':         'Match against quality-rejection log; chase credit note within 30 d.',
  'supplier-master-mismatch':'Halt payment; verify IBAN with supplier via verified channel.',
};

export function generateRecoveryAuditFindings(count = 24, seed = 0xa1_e0_c0): RecoveryAuditFinding[] {
  const rng = createRng(seed);
  const cats: RecoveryAuditCategory[] = [
    'duplicate-payment',
    'duplicate-invoice',
    'vat-recovery-gap',
    'price-variance',
    'currency-mismatch',
    'missing-gln',
    'late-tdd',
    'overpayment',
    'credit-note-due',
    'supplier-master-mismatch',
  ];
  const out: RecoveryAuditFinding[] = [];
  for (let i = 0; i < count; i++) {
    const cat = rng.pick(cats);
    const ageDays = rng.int(0, 90);
    const detectedAt = new Date(Date.now() - ageDays * 86400_000);
    const r = rng.next();
    const status: RecoveryAuditStatus =
      r < 0.55 ? 'open' : r < 0.75 ? 'in-review' : r < 0.93 ? 'recovered' : 'dismissed';
    const refs = Array.from({ length: rng.int(1, 3) }, () =>
      `INV-2026-${String(rng.int(1, 99)).padStart(2, '0')}-${String(rng.int(1, 9999)).padStart(4, '0')}`,
    );
    const recoverableAed = (() => {
      switch (cat) {
        case 'duplicate-payment':       return rng.int(1_200, 24_000);
        case 'overpayment':             return rng.int(2_500, 45_000);
        case 'vat-recovery-gap':        return rng.int(180, 3_400);
        case 'price-variance':          return rng.int(450, 12_000);
        case 'currency-mismatch':       return rng.int(120, 2_800);
        case 'late-tdd':                return rng.int(800, 1_200);
        case 'duplicate-invoice':       return rng.int(2_000, 18_000);
        case 'credit-note-due':         return rng.int(900, 5_500);
        case 'supplier-master-mismatch':return rng.int(0, 0); // money at risk, not yet recoverable
        case 'missing-gln':             return rng.int(0, 0);
      }
    })();
    out.push({
      findingId: `ra-${String(i + 1).padStart(4, '0')}`,
      category: cat,
      titleEn: TITLES[cat].en,
      titleAr: TITLES[cat].ar,
      currency: 'AED',
      recoverableAed,
      confidence: rng.pick(['low', 'medium', 'high']),
      status,
      invoiceRefs: refs,
      supplier: rng.pick(SUPPLIERS),
      detectedAt: detectedAt.toISOString(),
      ageDays,
      recommendation: RECOMMENDATIONS[cat],
    });
  }
  out.sort((a, b) => (a.detectedAt < b.detectedAt ? 1 : -1));
  return out;
}

export interface RecoveryAuditTotals {
  totalFindings: number;
  totalOpen: number;
  totalRecovered: number;
  /** Sum of recoverableAed across open + in-review. */
  pipelineAed: number;
  /** Sum of recoverableAed across status=recovered. */
  recoveredAed: number;
  /** Top categories by count. */
  topCategories: { category: RecoveryAuditCategory; count: number; aed: number }[];
}

export function summariseRecoveryAudit(findings: RecoveryAuditFinding[]): RecoveryAuditTotals {
  const counts = new Map<RecoveryAuditCategory, { count: number; aed: number }>();
  let pipelineAed = 0;
  let recoveredAed = 0;
  let totalOpen = 0;
  let totalRecovered = 0;
  for (const f of findings) {
    const slot = counts.get(f.category) ?? { count: 0, aed: 0 };
    slot.count += 1;
    slot.aed += f.recoverableAed;
    counts.set(f.category, slot);
    if (f.status === 'open' || f.status === 'in-review') {
      pipelineAed += f.recoverableAed;
      totalOpen += 1;
    } else if (f.status === 'recovered') {
      recoveredAed += f.recoverableAed;
      totalRecovered += 1;
    }
  }
  const topCategories = Array.from(counts.entries())
    .map(([category, v]) => ({ category, count: v.count, aed: v.aed }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);
  return {
    totalFindings: findings.length,
    totalOpen,
    totalRecovered,
    pipelineAed,
    recoveredAed,
    topCategories,
  };
}
